import { Fragment, ReactNode, memo, useEffect, useState } from "react";
import { TCartProduct, TProduct, useCart } from "./cart-provider";
import cartStyles from "./cart.module.scss";
import ProductCarousel from "./product-carousel";
import CaretDown from "assets/icons/CaretDown";

import { Collapse, Popover } from "@mantine/core";
import Plus from "assets/icons/Plus";
import Minus from "assets/icons/Minus";

const rounding = (num: number) => Math.round(num * 100) / 100;

const CartList = memo(() => {
  const cart = useCart();

  const { itemList, recipeList, total } = cart;

  // Only show recipes that have ingredients (completed recipes)
  const completedRecipes = recipeList.filter(
    (recipe) => recipe.ingredients?.length > 0
  );

  // Show cart only when there are products in itemList or completed recipes
  const shouldShowCart = itemList.length > 0 || completedRecipes.length > 0;

  // Set cart step to "cart" when products are present
  useEffect(() => {
    // Always set the cart step to "cart" when component mounts or items change
    cart.setStep("cart");
  }, [itemList.length, cart]);

  // Determine if total price should be shown or a loading indicator
  const showLoadingPrice = total === 0; // Only show loading when total is exactly 0

  return (
    <div className={cartStyles["cart__body"]}>
      {shouldShowCart && (
        <>
          {/* Replace CollapsibleItem with a static non-collapsible container */}
          <div
            style={{
              display: "flex",
              gap: 8,
              flexDirection: "column",
            }}
          >
            <div className={cartStyles["cart__header"]}>
              <span>Cart</span>
              <div className={cartStyles["cart__total-price"]}>
                {showLoadingPrice ? (
                  <div
                    className="neo-icon no-shadow"
                    style={{
                      height: "20px",
                      width: "20px",
                      display: "inline-flex",
                      padding: 0,
                      margin: 0,
                      verticalAlign: "middle",
                    }}
                  >
                    <svg
                      fill="none"
                      stroke="var(--primary-color)"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      width="20"
                      height="20"
                      className="recipe-panel__loading-icon"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                      ></path>
                    </svg>
                  </div>
                ) : (
                  `€${total}`
                )}
              </div>
            </div>
            <div>
              <div className={cartStyles["cart__list-container"]}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: "0 0",
                    width: "100%",
                    height: "100%",
                    overflowX: "hidden",
                    overflowY: "auto",
                  }}
                >
                  <List {...cart} />
                  {completedRecipes.map((recipe) => {
                    const swapItem = (id: string, product: TProduct) => {
                      cart.swapRecipeItem(recipe.id, id, product);
                    };

                    const increaseRecipeItemQuantity = (
                      id: string,
                      quantity?: number
                    ) => {
                      cart.increaseRecipeItemQuantity(recipe.id, id, quantity);
                    };

                    const decreaseRecipeItemQuantity = (
                      id: string,
                      quantity?: number
                    ) => {
                      cart.decreaseRecipeItemQuantity(recipe.id, id, quantity);
                    };

                    return (
                      <div key={recipe.id} style={{ marginTop: 12 }}>
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: 14,
                            marginBottom: 6,
                            paddingBottom: 4,
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          Rezept: {recipe.name}
                        </div>
                        <List
                          {...cart}
                          increaseQuantity={increaseRecipeItemQuantity}
                          decreaseQuantity={decreaseRecipeItemQuantity}
                          swapItem={swapItem}
                          itemList={
                            recipe.ingredients
                            //   .filter(
                            //   // Only show ingredients that aren't already in the main cart
                            //   (ingredient) =>
                            //     !itemList.some(
                            //       (item) =>
                            //         item.product.id === ingredient.product.id
                            //     )
                            // )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {shouldShowCart && <CartCheckout />}
    </div>
  );
});

const List = ({
  itemList,
  expandedOptions,
  setExpandedOptions,
  swapItem,
  increaseQuantity,
  decreaseQuantity,
}: ReturnType<typeof useCart>) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "0 0",
        width: "100%",
        height: "100%",
        overflowY: "visible",
      }}
    >
      {itemList.map((rawItem, index) => {
        const { product: item } = rawItem;

        let title = item.name;
        let prefix = "";

        if (item.brand?.name) {
          const short = item.brand.name.split(" ")[0];

          if (title.includes(short)) {
            title = title.split(short)[1];
            prefix = short;
          }
        }

        if (
          item.brand?.nameShort &&
          item.name.includes(item.brand?.nameShort)
        ) {
          title = item.name.split(item.brand?.nameShort as string)[1];
          prefix = item.brand.name;
        }

        return (
          <Fragment key={item.id}>
            <div>
              <div className={cartStyles["cart-item"]}>
                <ProductImage {...rawItem} />
                <div className={cartStyles["cart-item__body"]}>
                  <div className={cartStyles["cart-item__content"]}>
                    <div
                      className={cartStyles["cart-item__title"]}
                      onClick={() =>
                        setExpandedOptions((option) =>
                          option === item.id ? undefined : item.id
                        )
                      }
                    >
                      {prefix && (
                        <span className={cartStyles["cart-item__title-prefix"]}>
                          {prefix}
                        </span>
                      )}
                      <span style={{ lineHeight: 1.1 }}>{title}</span>{" "}
                      <CaretDown up={rawItem.product.id === expandedOptions} />
                    </div>
                    <div className={cartStyles["cart-item__details"]}>
                      <ProductPrice {...rawItem} />
                    </div>
                  </div>
                  <ProductQuantity
                    {...rawItem}
                    increaseQuantity={increaseQuantity}
                    decreaseQuantity={decreaseQuantity}
                  />
                </div>
              </div>
            </div>

            <div style={{ padding: "0px 8px" }}>
              <Collapse in={rawItem.product.id === expandedOptions}>
                <ProductCarousel
                  items={rawItem.options || []}
                  onItemClick={(item) => swapItem(rawItem.product.id, item)}
                />
              </Collapse>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};

const ProductImage = ({ product: item }: { product: TProduct }) => {
  return (
    <img
      src={item.images[0]}
      className={cartStyles["cart-item__image"]}
      alt={item.name}
    />
  );
};

function debounce<T extends (...args: any[]) => void>(func: T, wait = 100) {
  let timeoutId: NodeJS.Timeout | null;

  return function (this: any, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

const ProductQuantity = (
  props: TCartProduct & {
    increaseQuantity: ReturnType<typeof useCart>["increaseQuantity"];
    decreaseQuantity: ReturnType<typeof useCart>["decreaseQuantity"];
  }
) => {
  const { product: item, quantity, increaseQuantity, decreaseQuantity } = props;
  const [isOpen, setIsOpen] = useState(false);

  const button = (
    <div
      className={[
        cartStyles["cart-item__quantity-value"],
        isOpen ? cartStyles["cart-item__quantity-value_active"] : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {quantity}
    </div>
  );

  const debouncedOpen = debounce((isOpen: boolean) => setIsOpen(isOpen));

  return (
    <div className={cartStyles["cart-item__quantity"]}>
      <Popover
        position="top"
        opened={isOpen}
        offset={{ mainAxis: 0, crossAxis: 0 }}
        width={30}
        onChange={(opened) => {
          if (!opened) {
            debouncedOpen(false);
          }
        }}
      >
        <Popover.Dropdown
          style={{
            padding: 0,
            border: "none",
            "--background-color-secondary": "rgb(222, 222, 225)",
            "--background-color-secondary-light": "#e5f4f3",
            backgroundColor: "rgb(222, 222, 225)",
          }}
        >
          <button
            className={cartStyles["cart-item__quantity-increase"]}
            onClick={() => increaseQuantity(item.id)}
          >
            <Plus />
          </button>
        </Popover.Dropdown>
        <Popover.Target>{button}</Popover.Target>
      </Popover>

      <Popover
        position="bottom"
        opened={isOpen}
        offset={{ mainAxis: 0, crossAxis: 0 }}
        width={30}
      >
        <Popover.Target>
          <span
            style={{
              zIndex: -1,
              position: "absolute",
              width: 30,
              height: 30,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              visibility: "hidden",
            }}
          >
            {button}
          </span>
        </Popover.Target>
        <Popover.Dropdown
          style={{
            padding: 0,
            border: "none",
            "--background-color-secondary": "rgb(222, 222, 225)",
            "--background-color-secondary-light": "#e5f4f3",
            backgroundColor: "rgb(222, 222, 225)",
          }}
        >
          <button
            className={cartStyles["cart-item__quantity-decrease"]}
            onClick={() => {
              decreaseQuantity(item.id);
              setTimeout(() => debouncedOpen(true), 1);
            }}
          >
            <Minus />
          </button>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};

const ProductUnits = (props: TCartProduct) => {
  const { product: item, quantity } = props;

  const unitAmount = Number(item.unitAmount || 0);

  return (
    <div className={cartStyles["cart-item__units"]}>
      <div>
        {rounding(quantity * unitAmount)}
        {item.volumeLabelShort || item.volumeLabel || item.weightUnit || ""}
      </div>
    </div>
  );
};

const ProductPrice = (props: TCartProduct) => {
  const { product: item, quantity } = props;

  // Add fallback for baseFactor
  const baseFactor = Number(item.price?.baseFactor || 0);

  // Ensure price.value exists before using it
  const priceValue = item.price?.value ? item.price.value * quantity : 0;
  const fullValue = item.price?.fullValue ? item.price.fullValue * quantity : 0;
  const perUnit = item.price?.perUnit ? item.price.perUnit : 0;

  return (
    <div className={cartStyles["cart-item__price"]}>
      <div className={cartStyles["cart-item__price-value"]}>
        €{rounding(priceValue)}{" "}
      </div>
      {!!item.price?.fullValue && item.price.fullValue !== item.price.value && (
        <span
          style={{
            textDecoration: "line-through",
            color: "var(--text-color-secondary-light)",
          }}
        >
          €{rounding(fullValue)}
        </span>
      )}

      <CartTag active>
        <ProductUnits {...props} />
      </CartTag>
      {baseFactor ? (
        <CartTag>
          <div className={cartStyles["cart-item__base-factor"]}>
            <>
              <span>
                {baseFactor}
                {item.baseUnitShort || ""}
              </span>
              <span>€{perUnit}</span>
            </>
          </div>
        </CartTag>
      ) : null}
      {item.price?.discountPercentage ? (
        <CartTag primary>
          <div>{rounding(item.price.discountPercentage)}%</div>
        </CartTag>
      ) : null}
    </div>
  );
};

const CartTag = ({
  children,
  active,
  primary,
}: {
  children?: ReactNode;
  active?: boolean;
  primary?: boolean;
}) => {
  const className = [
    cartStyles["cart-item__tag"],
    primary
      ? cartStyles["cart-item__tag_primary"]
      : active
      ? cartStyles["cart-item__tag_active"]
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return <div className={className}>{children}</div>;
};

const CartCheckout = () => {
  // const { checkout } = useCart();

  return <></>;
};

export default CartList;
